import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, User, Check, Clock, Info, ChevronDown, ChevronUp, MessageSquare, Calendar, Users } from 'lucide-react';
import { fetchRfInboxRequest, updateSkillEvalRequest, rfDecisionRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import Swal from 'sweetalert2';
import '../EvaluationDesign.css';

const NIVEAUX = [
    { value: 1, label: "Débutant" },
    { value: 2, label: "Intermédiaire" },
    { value: 3, label: "Avancé" },
    { value: 4, label: "Expert" }
];

export default function EvalRh() {
    const dispatch = useDispatch();
    const { inboxRf, loading } = useSelector(state => state.skillEvaluation || { inboxRf: [] });
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { user } = useSelector(state => state.auth || {});

    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [filterStatus, setFilterStatus] = useState("EN_ATTENTE_RF");
    const [pendingDecision, setPendingDecision] = useState(null);
    const [globalComment, setGlobalComment] = useState("");
    
    const [rfEvals, setRfEvals] = useState({});
    const [rfComments, setRfComments] = useState({});
    const [expandedAxes, setExpandedAxes] = useState(["AUTRES"]);

    useEffect(() => {
        dispatch(fetchRfInboxRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    const activeCampagnes = useMemo(() => {
        return (campagnesList || []).filter(c => c.status === true);
    }, [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    useEffect(() => {
        if (selectedUser) {
            const evals = {};
            const comments = {};
            selectedUser.niveaux?.forEach(n => {
                evals[n.skillId] = n.scoreObtenu || n.scoreManager || n.scoreCollab || 1; 
                comments[n.skillId] = n.commentaire || "";
            });
            setRfEvals(evals);
            setRfComments(comments);
            setGlobalComment(selectedUser.rfComment || "");
        }
    }, [selectedUser]);

    useEffect(() => {
        if (pendingDecision && !loading) {
            dispatch(rfDecisionRequest(pendingDecision.id, pendingDecision.dto));
            setPendingDecision(null);
            setSelectedUser(null);
        }
    }, [loading, pendingDecision, dispatch]);

    const toggleAxe = (axeId) => {
        setExpandedAxes(p => p.includes(axeId) ? p.filter(id => id !== axeId) : [...p, axeId]);
    };

    const handleEval = (compId, level) => {
        setRfEvals(p => ({ ...p, [compId]: level }));
    };

    const handleComment = (compId, text) => {
        setRfComments(p => ({ ...p, [compId]: text }));
    };

    const handleApprove = () => {
        if (!selectedUser) return;
        
        let confirmText = selectedUser.managerStepStatus === 'APPROVED' ? 
            "Confirmez-vous l'approbation finale de cette évaluation (déjà validée par le manager) ?" : 
            "Confirmez-vous la validation finale de l'évaluation de ce collaborateur ?";

        Swal.fire({
            title: 'Validation Finale',
            text: confirmText,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#204385',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Oui, clôturer',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                const niveauxArray = Object.entries(rfEvals).map(([skillId, scoreObtenu]) => ({
                    skillId: parseInt(skillId),
                    scoreObtenu: parseInt(scoreObtenu),
                    commentaire: rfComments[skillId] || ""
                }));

                const updateDto = {
                    campagneId: selectedUser.campagneId,
                    userId: selectedUser.userId,
                    statut: selectedUser.statut,
                    evaluateurId: user?.id || user?.uid,
                    dateEvaluation: selectedUser.dateEvaluation,
                    commentaireGlobal: selectedUser.commentaireGlobal,
                    niveaux: niveauxArray
                };

                const decisionDto = {
                    approved: true,
                    comment: globalComment || "Évaluation clôturée et validée par le service RH."
                };

                setPendingDecision({ id: selectedUser.id, dto: decisionDto });
                dispatch(updateSkillEvalRequest(selectedUser.id, updateDto));
            }
        });
    };

    const getStatusConfig = (statut) => {
        const s = typeof statut === 'string' ? statut : statut?.name;
        switch (s) {
            case "EN_ATTENTE_COLLAB": return { label: "Attente Collab.", badgeClass: "eval-badge-grey", icon: <Clock size={12} /> };
            case "EN_ATTENTE_MANAGER": return { label: "Attente Manager", badgeClass: "eval-badge-grey", icon: <Clock size={12} /> };
            case "EN_ATTENTE_RF": return { label: "À traiter (RF)", badgeClass: "eval-badge-red", icon: <Info size={12} /> };
            case "VALIDEE": return { label: "Validé (Terminée)", badgeClass: "eval-badge-green", icon: <Check size={12} /> };
            default: return { label: s, badgeClass: "eval-badge-grey", icon: null };
        }
    };

    const filteredUsers = useMemo(() => {
        return (inboxRf || []).map(e => ({
            ...e,
            id: e.id,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            poste: e.userEmployeeCode || "Collaborateur",
            color: (typeof e.statut === 'string' ? e.statut : e.statut?.name) === 'VALIDEE' ? '#10b981' : '#204385'
        })).filter(u => {
            const uStatut = typeof u.statut === 'string' ? u.statut : u.statut?.name;
            const matchSearch = u.nom?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
            const matchStatus = filterStatus === "ALL" || uStatut === filterStatus;
            const matchCampagne = !selectedCampagneId || u.campagneId === parseInt(selectedCampagneId);
            return matchSearch && matchStatus && matchCampagne;
        });
    }, [inboxRf, searchQuery, filterStatus, selectedCampagneId]);

    const formattedAxes = useMemo(() => {
        if (!selectedUser || !selectedUser.niveaux) return [];
        const grouped = selectedUser.niveaux.reduce((acc, n) => {
            const key = "AUTRES"; 
            if (!acc[key]) {
                acc[key] = { id: key, nom: "Compétences de l'évaluation", competences: [] };
            }
            acc[key].competences.push({
                id: n.skillId,
                nom: n.skillNom,
                description: n.skillCode || "Code compétence",
                scoreCollab: n.scoreCollab,
                scoreManager: n.scoreManager,
                scoreRf: n.scoreRf,
                autoEvalComment: n.commentaire || "",
            });
            return acc;
        }, {});
        return Object.values(grouped);
    }, [selectedUser]);

    return (
        <div className="eval-page">
            <div className="eval-header">
                <div>
                    <h1>Validation Finale RH</h1>
                    <p>Interface de clôture et de consolidation des évaluations annuelles.</p>
                </div>
                <div className="eval-filter-tabs" style={{ maxWidth: '300px' }}>
                    <button className={`eval-tab-btn ${filterStatus === 'EN_ATTENTE_RF' ? 'active' : ''}`} onClick={() => setFilterStatus('EN_ATTENTE_RF')}>À arbitrer</button>
                    <button className={`eval-tab-btn ${filterStatus === 'VALIDEE' ? 'active' : ''}`} onClick={() => setFilterStatus('VALIDEE')}>Arbitrées</button>
                </div>
            </div>

            <div className="eval-campaign-bar">
                <span className="eval-campaign-label">Campagne active :</span>
                <select className="eval-select" value={selectedCampagneId} onChange={(e) => setSelectedCampagneId(e.target.value)}>
                    <option value="">-- Toutes les campagnes --</option>
                    {activeCampagnes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom} {c.anneeCampagne ? `(${c.anneeCampagne})` : ''}</option>
                    ))}
                </select>
            </div>

            <div className="eval-layout">
                <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search className="eval-search-icon" size={16} />
                            <input type="text" className="eval-input" placeholder="Rechercher un dossier..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                        </div>
                    </div>
                    
                    <div className="eval-list">
                        {filteredUsers.length === 0 ? (
                            <div className="eval-empty" style={{ padding: '20px' }}>
                                <Users size={32} className="eval-empty-icon" />
                                <p style={{ fontSize: '12px' }}>Aucun dossier trouvé</p>
                            </div>
                        ) : (
                            filteredUsers.map(u => (
                                <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                    <div className="eval-avatar" style={{ background: '#ee645f' }}>
                                        {u.nom?.charAt(0) || 'U'}
                                    </div>
                                    <div className="eval-user-info">
                                        <div className="eval-user-name">{u.nom}</div>
                                        <div className="eval-user-sub">{u.poste || "Poste inconnu"}</div>
                                    </div>
                                    {(typeof u.statut === 'string' ? u.statut : u.statut?.name) === 'VALIDEE' ? <Check size={14} style={{ color: '#ee645f' }} /> : <Clock size={14} style={{ color: '#94a3b8' }} />}
                                </div>
                            ))
                        )}
                    </div>
                </aside>

                <main className="eval-main">
                    {!selectedUser ? (
                        <div className="eval-empty">
                            <Check size={64} className="eval-empty-icon" />
                            <h3>Prêt pour la clôture ?</h3>
                            <p>Sélectionnez un dossier à traiter dans la colonne de gauche.</p>
                        </div>
                    ) : (
                        <div className="eval-content">
                            <div className="eval-card" style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                        <div className="eval-avatar" style={{ width: '56px', height: '56px', fontSize: '20px', backgroundColor: '#204385' }}>
                                            {selectedUser.nom?.charAt(0)}
                                        </div>
                                        <div>
                                            <h2 style={{ margin: 0, color: '#204385' }}>{selectedUser.nom}</h2>
                                            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>{selectedUser.poste} • {selectedUser.campagneNom}</p>
                                        </div>
                                    </div>
                                    { (typeof selectedUser.statut === 'string' ? selectedUser.statut : selectedUser.statut?.name) === 'EN_ATTENTE_RF' && (
                                        <button className="eval-btn eval-btn-coral" onClick={handleApprove} disabled={loading}>
                                            <Check size={16} /> Clôturer et Valider
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <MessageSquare size={18} /> Commentaire Final RH
                                </div>
                                <textarea 
                                    className="eval-textarea" 
                                    placeholder="Saisissez la conclusion RH pour cette évaluation..."
                                    value={globalComment}
                                    onChange={(e) => (typeof selectedUser.statut === 'string' ? selectedUser.statut : selectedUser.statut?.name) === 'EN_ATTENTE_RF' && setGlobalComment(e.target.value)}
                                    readOnly={(typeof selectedUser.statut === 'string' ? selectedUser.statut : selectedUser.statut?.name) !== 'EN_ATTENTE_RF'}
                                    rows={3}
                                />
                            </div>

                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <Info size={18} /> Récapitulatif et Arbitrage
                                </div>
                                
                                {formattedAxes.map(axe => (
                                    <div key={axe.id} style={{ marginBottom: '24px' }}>
                                        {axe.competences.map(comp => (
                                            <div key={comp.id} className="eval-card" style={{ padding: '0', overflow: 'hidden', marginBottom: '12px' }}>
                                                <div style={{ padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>{comp.skillNom}</span>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <div className="eval-badge eval-badge-grey">Auto: {comp.scoreCollab || "?"}</div>
                                                        <div className="eval-badge eval-badge-blue">Manager: {comp.scoreManager || "?"}</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="eval-grid" style={{ gridTemplateColumns: '1.2fr 1fr', padding: '12px 16px', gap: '20px' }}>
                                                    <div className="eval-collab-view" style={{ padding: '10px', fontSize: '11px' }}>
                                                        <div style={{ marginBottom: '4px' }}><strong>Obs Collab:</strong> {comp.autoEvalComment || "N/A"}</div>
                                                        <div><strong>Obs Manager:</strong> {comp.managerComment || "N/A"}</div>
                                                    </div>

                                                    <div className="eval-manager-view">
                                                        <div className="eval-input-group">
                                                            {NIVEAUX.map(niv => (
                                                                <button key={niv.value} className={`eval-choice-btn ${rfEvals[comp.id] === niv.value ? 'active' : ''}`} onClick={() => handleEval(comp.id, niv.value)}>
                                                                    {niv.value}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        <textarea className="eval-textarea" placeholder="Note RH finale..." value={rfComments[comp.id] || ""} onChange={(e) => handleComment(comp.id, e.target.value)} rows={1} style={{ minHeight: '38px', marginTop: '8px' }} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
